import { Check, LoaderCircle, Plus, Search } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import type { Customer } from "./types";

type CustomerPickerProps = {
  customers: Customer[];
  selectedCustomer: Customer | null;
  value: string;
  loading: boolean;
  error?: string;
  onChange: (customerId: string) => void;
  onSearchChange: (query: string) => void;
  createHref: string;
};

export function CustomerPicker({ customers, selectedCustomer, value, loading, error, onChange, onSearchChange, createHref }: CustomerPickerProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selectedCustomer?.legalName ?? "");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (value && selectedCustomer) setQuery(selectedCustomer.legalName);
    if (!value && !open) setQuery("");
  }, [open, selectedCustomer, value]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const selectCustomer = (customer: Customer) => {
    setQuery(customer.legalName);
    setOpen(false);
    onChange(customer.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") return setOpen(false);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, customers.length - 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    }
    if (event.key === "Enter" && open && customers[activeIndex]) {
      event.preventDefault();
      selectCustomer(customers[activeIndex]);
    }
  };

  return <div className="customer-picker-field commercial-wide" ref={rootRef}>
    <span className="customer-picker-label">Cliente</span>
    <div className="customer-picker-control">
      <div className="customer-picker-input">
        <Search size={17} aria-hidden="true" />
        <input
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && customers[activeIndex] ? `${listId}-${customers[activeIndex].id}` : undefined}
          autoComplete="off"
          placeholder="Buscar cliente"
          value={query}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setQuery(nextQuery);
            setOpen(true);
            setActiveIndex(0);
            if (value) onChange("");
            onSearchChange(nextQuery);
          }}
          required
        />
        {loading && <LoaderCircle className="customer-picker-loading" size={17} aria-label="Buscando clientes" />}
      </div>
      <Link className="customer-create-shortcut" to={createHref} aria-label="Adicionar cliente" title="Adicionar cliente"><Plus size={20} /></Link>
      {open && <div className="customer-picker-menu" id={listId} role="listbox">
        {error && <div className="customer-picker-message">Não foi possível buscar clientes.</div>}
        {!error && !loading && customers.length === 0 && <div className="customer-picker-message">Nenhum cliente encontrado.</div>}
        {customers.map((customer, index) => <button
          id={`${listId}-${customer.id}`}
          key={customer.id}
          type="button"
          role="option"
          aria-selected={customer.id === value}
          className={index === activeIndex ? "active" : ""}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => selectCustomer(customer)}
        ><span><strong>{customer.legalName}</strong>{customer.tradeName && customer.tradeName !== customer.legalName && <small>{customer.tradeName}</small>}</span>{customer.id === value && <Check size={16} />}</button>)}
      </div>}
    </div>
  </div>;
}
